import { Stack } from 'component-library';
import { useTranslation } from 'react-i18next';

import { ChartHeading } from '@/components/Charts/components/ChartHeading';
import {
  EcologicalIntegrityChartData,
  EcologicalIntegrityHistoricalChart,
} from '@/pages/shared/projects/pages/project/pages/conservation/BU/components/Charts/EcologicalIntegrityHistorical';
import { useGetConservationBenchmarks } from '@/pages/shared/projects/pages/project/pages/conservation/BU/hooks/useGetConservationBenchmarks';
import { useGetTrendLabel } from '@/pages/shared/projects/pages/project/pages/conservation/BU/hooks/useGetTrendLabel';
import { getBenchmarkForConservationFact } from '@/pages/shared/projects/pages/project/pages/conservation/BU/utils/getBenchmarkForConservationFact';
import { useFact } from '@/pages/shared/projects/pages/project/pages/conservation/shared/hooks/useFact';

export const GraphTile = () => {
  const { t } = useTranslation();

  const projectAverageIs = useFact<number>('r1_conservation_indicator_species_score');
  const projectAverageEi = useFact<number>('r1_conservation_ecological_integrity');
  const projectAverage = projectAverageIs?.value ? projectAverageIs : projectAverageEi;
  const eiDataRange: [number, number] = useFact<[number, number]>('r1_conservation_ecological_integrity_data_range')
    ?.value ?? [0, 100];
  const eiRangeUpperBound = eiDataRange?.[1];

  const benchmarks = useGetConservationBenchmarks(eiRangeUpperBound);
  const benchmark = getBenchmarkForConservationFact({
    fact: projectAverage,
    benchmarks,
  });

  const trendLabel = useGetTrendLabel({
    trendChangeFact: 'r1_conservation_ecological_integrity_change_percentage',
  });

  const isBaselineYear = useFact<{ value: number }>('r1_conservation_indicator_species_baseline_year').value;
  const eiBaselineYear = useFact<{ value: number }>('r1_conservation_ecological_integrity_baseline_year').value;
  const hiBaselineYear = useFact<{ value: number }>('r1_conservation_habitat_intactness_baseline_year').value;
  const baselineYears = [isBaselineYear, eiBaselineYear, hiBaselineYear].filter(
    (year) => year !== undefined && year !== null,
  ) as unknown as number[];

  const baselineYear = Math.min(...baselineYears);

  const ecologicalIntegrityTimeSeries =
    useFact<Record<string, number>>('r1_conservation_ecological_integrity_ts')?.value ?? {};
  const habitatIntactnessTimeSeries = useFact<Record<string, number>>('r1_conservation_threats_i_ts')?.value ?? {};
  const indicatorSpeciesTimeSeries = useFact<Record<string, number>>('r1_conservation_indicator_species_ts')?.value;
  const speciesOccupancyTimeSeries = useFact<Record<string, number>>(
    'r1_conservation_species_occupancy_index_ts',
  )?.value;

  const speciesTimeSeries = (indicatorSpeciesTimeSeries || speciesOccupancyTimeSeries) ?? {};

  const years = [
    ...Object.keys(ecologicalIntegrityTimeSeries),
    ...Object.keys(habitatIntactnessTimeSeries),
    ...Object.keys(speciesTimeSeries),
  ]
    .reduce((acc, year) => (acc.includes(year) ? acc : [...acc, year]), [] as string[])
    .sort((a, b) => parseInt(a, 10) - parseInt(b, 10));

  const chartData: EcologicalIntegrityChartData[] = years.map((year) => ({
    ecologicalIntegrity: ecologicalIntegrityTimeSeries[`${year}`] ?? null,
    habitatIntactness: habitatIntactnessTimeSeries[`${year}`] ?? null,
    indicatorSpecies: speciesTimeSeries[`${year}`] ?? null,
    date: new Date(year),
    benchmark: benchmark ? benchmarks[`${benchmark}`] : null,
  }));

  const chartBaseYear = chartData
    ?.sort((a, b) => a.date.getTime() - b.date.getTime())
    ?.at(0)
    ?.date?.getFullYear();

  return (
    <Stack center spacing={6} data-testid='ecological-integrity-graph-tile'>
      <ChartHeading
        heading={t('shared.ncaDetail.ecologicalIntegrity.historicalDataBU.chartHeading', {
          year: chartBaseYear ?? baselineYear,
        })}
        className='w-full px-6 sm:px-0'
      />
      <EcologicalIntegrityHistoricalChart
        data={chartData}
        benchmark={benchmark}
        trendLabel={trendLabel}
        eiDataRange={eiDataRange}
      />
    </Stack>
  );
};
